import React, { FC, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import Destinations from "Components/Destinations";
import { setShowSearchOptions, setView } from "Containers/CampusMap";
import { areEqual } from "Utils/areEqual";
import {
  setCurrentStop,
  setStopState,
  StopState,
  setStops,
} from "Containers/Stops";
import { setDestinationsView, SetDestinationsViewTriggerType } from "./actions";

interface Props {
  trailsLength: number;
  googleMap: any;
  showSwitcher: boolean;
  showSearchAI: boolean;
  setShowSearchPrompt: () => void;
}
const DestinationsContainer: FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const {
    trailsLength,
    googleMap,
    showSwitcher,
    showSearchAI,
    setShowSearchPrompt,
  } = props;

  const destinations = useSelector(({ stops }: any) => stops, areEqual);
  const view = useSelector((store: any) => store.searchOptionsView.view);
  const hasMounted = useRef(false);
  const { personalizedStops: personalizedStopsValue } = useSelector(
    ({ personalizedStops }: any) => personalizedStops,
    areEqual
  );

  const { showDestinations, selectedDestination } = useSelector(
    (selectors: any) => selectors.destinations,
    areEqual
  );

  const { showSearchOptions } = useSelector(
    ({ searchOptionsView }: any) => searchOptionsView,
    areEqual
  );

  const currentStop = useSelector(
    (selectors: any) => selectors.currentStop.currentStop,
    areEqual
  );

  const orderPersonalizedStops = useCallback(() => {
    const stopIdsToFilter = personalizedStopsValue.map(
      (stop: any) => stop.stopid
    );
    const stopRanks = personalizedStopsValue.reduce((ranks, stop) => {
      return { ...ranks, [stop.stopid]: stop.rank };
    }, {});

    const orderedPersonalizedStops = destinations
      .filter((stop) => stopIdsToFilter?.includes(stop.stopid))
      .map((stop) => ({ isPersonalized: true, ...stop }))
      .sort((a, b) => {
        const rankA = stopRanks[a.stopid] || 0;
        const rankB = stopRanks[b.stopid] || 0;
        return rankB - rankA;
      });

    const orderedStops = [...orderedPersonalizedStops, ...destinations];
    return { orderedStops, orderedPersonalizedStops };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [personalizedStopsValue]);

  const loadPersonalizedStops = useCallback(() => {
    const { orderedStops } = orderPersonalizedStops();
    dispatch(setStops(orderedStops));
  }, [dispatch, orderPersonalizedStops]);

  useEffect(() => {
    if (personalizedStopsValue) {
      loadPersonalizedStops();
    }
  }, [personalizedStopsValue, loadPersonalizedStops]);

  // This is used for the click events in the search options icons to display the proper view
  const setSearchOptionsView = useCallback(
    (option: string) => {
      dispatch(setView(option));
    },
    [dispatch]
  );

  const showHideList = useCallback(
    (trigger: SetDestinationsViewTriggerType) => {
      if (showDestinations) {
        dispatch(setDestinationsView(false, trigger));
        dispatch(setShowSearchOptions(false));
      } else {
        dispatch(setDestinationsView(true, trigger));
        dispatch(setShowSearchOptions(true));
      }

      if (!showDestinations) {
        dispatch(setStopState([currentStop], StopState.Visited));
        dispatch(setCurrentStop(-1));
      }

      // If it's mobile, we want to show/hide the mobile buttons
      // dispatch(setShowSearchOptions(false));
    },
    [dispatch, showDestinations, currentStop]
  );

  useEffect(() => {
    // don't want focus to be on Destinations on intial load
    if (hasMounted.current) {
      const destinationsView = document.getElementById("current-label-button");
      if (view === "destinations" && destinationsView) {
        destinationsView.focus();
      }
    } else {
      hasMounted.current = true;
    }
  }, [showDestinations, view, hasMounted]);

  return (
    <Destinations
      destinations={destinations}
      setSearchOptionsView={setSearchOptionsView}
      showHideList={showHideList}
      showDestinations={showDestinations}
      selectedDestination={selectedDestination}
      currentStop={currentStop}
      showSearchOptions={showSearchOptions}
      trailsLength={trailsLength}
      googleMap={googleMap}
      showSwitcher={showSwitcher}
      personalizedStops={personalizedStopsValue}
      showSearchAI={showSearchAI}
      setShowSearchPrompt={setShowSearchPrompt}
      view={view}
    />
  );
};

export default DestinationsContainer;
try {
    // @ts-ignore
    Destinations.displayName = "Destinations";
    // @ts-ignore
    Destinations.__docgenInfo = { "description": "", "displayName": "Destinations", "props": { "trailsLength": { "defaultValue": null, "description": "", "name": "trailsLength", "required": true, "type": { "name": "number" } }, "googleMap": { "defaultValue": null, "description": "", "name": "googleMap", "required": true, "type": { "name": "any" } }, "showSwitcher": { "defaultValue": null, "description": "", "name": "showSwitcher", "required": true, "type": { "name": "boolean" } }, "showSearchAI": { "defaultValue": null, "description": "", "name": "showSearchAI", "required": true, "type": { "name": "boolean" } }, "setShowSearchPrompt": { "defaultValue": null, "description": "", "name": "setShowSearchPrompt", "required": true, "type": { "name": "() => void" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/containers/Destinations/Destinations.tsx#Destinations"] = { docgenInfo: Destinations.__docgenInfo, name: "Destinations", path: "src/shared/containers/Destinations/Destinations.tsx#Destinations" };
}
catch (__react_docgen_typescript_loader_error) { }